import { useRowSelect } from '@table-library/react-table-library/select';
import { Action, State } from '@table-library/react-table-library/types/common';
import { Button, DropdownMenu, TableItem } from 'components/atoms';
import { LabeledStatus } from 'components/molecules';
import Popup from 'components/molecules/popup';
import { Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useFormikContext } from 'formik';
import { useComponentVisible, useHasPermission } from 'hooks';
import { IFormikContextType } from 'hooks/useFormikField';
import { useParamId } from 'hooks/useParamId';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { useTranslation } from 'react-i18next';
import { PiFilePlus, PiScroll } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { PopupActions } from 'reactjs-popup/dist/types';
import {
  useCreateContractFromTemplateForLabel,
  useGetContractsByLabel,
} from 'services/contract';
import {
  IContractListItemResponse,
  ICreateContractFromTemplateForm,
} from 'services/contract/types';
import { ILabelForm } from 'services/labels/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { formatDate } from 'utils/global/date.utils';
import { getNodes } from 'utils/table/nodes.utils';

const LabelContracts = () => {
  const labelId = useParamId('label');
  const hasPermission = useHasPermission();

  usePermissionGuard('ContractPermissionsLIST**');

  const { t } = useTranslation();
  const navigate = useNavigate();

  const { status, values }: IFormikContextType<ILabelForm> =
    useFormikContext<ILabelForm>();

  const {
    data,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch,
  } = useGetContractsByLabel(labelId, { params: { ShowOnlyActive: false } });

  const createContract = useCreateContractFromTemplateForLabel();

  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false);

  const tableData = getNodes<IContractListItemResponse>(data?.pages.flat());

  const onSelectChange = (action: Action, state: State) => {
    navigate(`/label/${labelId}/contract/${action.payload.id}/form`);
  };

  const select = useRowSelect(tableData, {
    onChange: onSelectChange,
  });

  const goToCreateContract = () => navigate('create/form');

  const onAddContractFromTemplate = async (
    contract: ICreateContractFromTemplateForm,
    actions: PopupActions
  ) => {
    const { withEndDate, ...contractFromTemplate } = {
      ...contract,
      endDate:
        contract.withEndDate && contract.endDate
          ? new Date(contract.endDate).toISOString()
          : null,
      startDate: new Date(contract.startDate).toISOString(),
      labelId,
    };

    await createContract.mutateAsync(contractFromTemplate, {
      onSuccess: async () => {
        actions.close();
        await refetch();
      },
    });
  };

  return (
    <TopbarPage
      topbar={
        <TopBar
          size='compact'
          customBackItem={<></>}
          topbarContent={
            <>
            {hasPermission('ContractPermissionsLIST**', 1) && (
            <Button
              variant={BUTTON_VARIANT.submit}
              onClick={handleComponentVisible}
              outsideChildren={
                <DropdownMenu
                  ref={ref}
                  visible={isComponentVisible}
                  style={{ padding: 0 }}
                  justifyPosition='end'>
                  <Popup.AddContractFromTemplate
                    onAddClick={onAddContractFromTemplate}
                    trigger={
                      <Button isClear fullWidth>
                        <Button.Icon icon={<PiScroll />} />
                        <Button.Text
                          text={t('label.contract.add-with-template')}
                        />
                      </Button>
                    }
                  />
                  <Button isClear fullWidth onClick={goToCreateContract}>
                    <Button.Icon icon={<PiFilePlus />} />
                    <Button.Text text={t('label.contract.add-from-scratch')} />
                  </Button>
                </DropdownMenu>
              }>
              <Button.Text text={t('button.add')} />
              </Button>
            )}
            </>
          }
        />
      }>
      <Table
        id={'LabelContracts'}
        size='compact'
        data={tableData}
        select={select}
        displayMode
        columns={[
          {
            label: t('input.label.title'),
            renderCell: ({ title }) => <TableItem>{title}</TableItem>,
            resize: true,
          },
          {
            label: t('input.label.startDate'),
            renderCell: ({ startDate }) => (
              <TableItem>{formatDate(startDate)}</TableItem>
            ),
            resize: true,
          },
          {
            label: t('input.label.endDate'),
            renderCell: ({ endDate }) => (
              <TableItem>{endDate ? formatDate(endDate) : '-'}</TableItem>
            ),
            resize: true,
          },
          {
            label: t('input.label.status'),
            renderCell: ({ status }) => (
              <TableItem justifyPosition='center'>
                <LabeledStatus.Contract status={status} />
              </TableItem>
            ),
          },
        ]}
        loading={isFetchingNextPage || isLoading}
        onFullyScroll={fetchNextPage}
      />
    </TopbarPage>
  );
};

export default LabelContracts;
